import { useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { InternationalPhoneInputStyled } from "./InternationalPhoneInput.styled";
import "react-phone-input-2/lib/style.css";
import {
  TInputId,
  TInputReadoutValue,
} from "@ecp-redux/dto/themeSettings/themeSettings.types";
import { useLanguageCurrencyContext } from "@ecp-boxes/structure/Contexts/LanguageCurrencyContext";
import {
  Controller,
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { IElementInput } from "@ecp-redux/dto/themeSettings/settingsPatterns.types";
import {
  StyledInputLabel,
  StyledInputValidation,
} from "../../Input/StyledInput/StyledInput";
import { useRouter } from "next/router";
import { mapLanguageToLocale } from "@ecp-redux/api/tenantHelpers";
import { isClientSide } from "@ecp-boxes/helpers/helpers";
interface IInternationalPhoneInputProps<T extends FieldValues>
  extends UseControllerProps<T> {
  $settings: IElementInput | TInputId | TInputReadoutValue;
  label: string;
  className?: string;
  placeholder?: string;
  reactToLocaleChanges?: boolean;
}

const InternationalPhoneInput = <T extends FieldValues>({
  $settings,
  control,
  name,
  label,
  className,
  placeholder,
  reactToLocaleChanges = false,
}: IInternationalPhoneInputProps<T>) => {
  const [inputYOffset, setInputYOffset] = useState(0);
  const [inputTouched, setInputTouched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollWrapper = isClientSide()
    ? document.getElementById("scroll-wrapper")
    : null;
  const { storageLocale } = useLanguageCurrencyContext();
  const [, country] = storageLocale.split("_");
  const [initialCountry, setInitialCountry] = useState(country.toLowerCase());
  const { locale: routerLocale } = useRouter();
  const { field } = useController({ control, name });

  const parsedRouterLocale = routerLocale
    ? mapLanguageToLocale(routerLocale)
    : null;

  useEffect(() => {
    if (!inputTouched || reactToLocaleChanges) {
      if (parsedRouterLocale && parsedRouterLocale !== "default") {
        setInitialCountry(parsedRouterLocale);
      } else {
        setInitialCountry(country.toLowerCase());
      }
    }
  }, [country, inputTouched, reactToLocaleChanges, parsedRouterLocale]);

  useEffect(() => {
    if (!scrollWrapper) return;
    const controller = new AbortController();
    const { signal } = controller;

    const handleScroll = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        const bottomEdgePosition = rect.y + rect.height;
        setInputYOffset(bottomEdgePosition);
      }
    };
    handleScroll();
    scrollWrapper.addEventListener("scroll", handleScroll, { signal });

    return () => controller.abort();
  }, [scrollWrapper]);

  useEffect(() => {
    if (field.value) {
      const parsedValue = field.value.replace(/[+\s]/g, ""); // Removes plus sign and spaces
      field.onChange(parsedValue);
    }
  }, []);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <InternationalPhoneInputStyled
          $settings={$settings}
          formNoValidate={!fieldState.error}
        >
          <>
            <StyledInputLabel
              $settings={$settings}
              className={
                className ? `${className}__label` : "input-text__label"
              }
            >
              {label}
            </StyledInputLabel>
            <PhoneInput
              country={initialCountry}
              placeholder={placeholder}
              inputClass="international-phone-input"
              dropdownClass="international-phone-dropdown"
              {...field}
              onChange={(value) => {
                field.onChange(value);
                if (!inputTouched) {
                  setInputTouched(true);
                }
                if (country && inputRef.current) {
                  inputRef.current.focus();
                }
              }}
              inputProps={{
                ref: inputRef,
                onFocus: () => {
                  if (!inputTouched) {
                    setInputTouched(true);
                  }
                },
              }}
              autoFormat
              dropdownStyle={{
                top: inputYOffset,
              }}
            />

            {fieldState.error !== undefined && (
              <StyledInputValidation
                $settings={$settings}
                role="alert"
                className={
                  className
                    ? `${className}__validation`
                    : "input-text__validation"
                }
              >
                {fieldState.error.message}
              </StyledInputValidation>
            )}
          </>
        </InternationalPhoneInputStyled>
      )}
    />
  );
};

export default InternationalPhoneInput;
